import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SubscriptionState, SubscriptionStatus, SubscriptionTier, Subscription } from './subscriptionTypes';
import { fetchSubscriptions, updateSubscription, assignSubscription } from './subscriptionThunks';
import { logoutUser } from '@/features/auth/authThunks';

const initialState: SubscriptionState = {
  subscriptions: [],
  selectedSubscription: null,
  isLoading: false,
  isUpdating: false,
  error: null,
  statusFilter: 'all',
  tierFilter: 'all',
};

const subscriptionSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    // Set status filter for subscriptions list
    setStatusFilter: (state, action: PayloadAction<SubscriptionStatus | 'all'>) => {
      state.statusFilter = action.payload;
    },

    // Set tier filter for subscriptions list
    setTierFilter: (state, action: PayloadAction<SubscriptionTier | 'all'>) => {
      state.tierFilter = action.payload;
    },

    // Set selected subscription for editing
    setSelectedSubscription: (state, action: PayloadAction<Subscription | null>) => {
      state.selectedSubscription = action.payload;
    },

    // Clear subscription error
    clearSubscriptionError: (state) => {
      state.error = null;
    },

    // Clear all subscription state
    clearSubscriptionState: () => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchSubscriptions
      .addCase(fetchSubscriptions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to fetch subscriptions';
      })

      // Handle updateSubscription
      .addCase(updateSubscription.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update the subscription in the list (API returns subscription directly)
        // Preserve user info since API response doesn't include it
        const index = state.subscriptions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          const existingUser = state.subscriptions[index].user;
          state.subscriptions[index] = {
            ...action.payload,
            user: existingUser,
          };
        }
        state.selectedSubscription = null;
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.isUpdating = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to update subscription';
      })

      // Handle assignSubscription
      .addCase(assignSubscription.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(assignSubscription.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Add or update the subscription in the list
        const index = state.subscriptions.findIndex((s) => s.id === action.payload.subscription.id);
        if (index !== -1) {
          state.subscriptions[index] = action.payload.subscription;
        } else {
          state.subscriptions.unshift(action.payload.subscription);
        }
      })
      .addCase(assignSubscription.rejected, (state, action) => {
        state.isUpdating = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || action.error.message || 'Failed to assign subscription';
      })

      // Clear state on logout
      .addCase(logoutUser.fulfilled, () => {
        return { ...initialState };
      });
  },
});

export const {
  setStatusFilter,
  setTierFilter,
  setSelectedSubscription,
  clearSubscriptionError,
  clearSubscriptionState,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
